import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { handleApiError } from "../../models/store/storeEffects";
import { WorkTimeType } from "../../models/workShitTimeSlot/workTimeType";
import {
    fetchReportCostCenters,
    fetchReportCustomers,
    fetchReportEmployees,
    fetchReportSalaryRowTypes,
    fetchReportVehicles,
    fetchReportWorkTimeTypes,
    fetchReportEmployeeGroups,
} from "../../store/reportOptionsSlice";
import { useAsyncThunkAction } from "../useAsyncThunkAction";
import { fetchCalendarDays } from "../../store/calendarSlice";
import { EmployeeListItemDto } from "../../models/employee/employee";
import { ICostCenterItem } from "../../models/costCenter/costCenterItem";
import { VehicleListItemDto } from "../../models/transport/vehicle";
import { SalaryRowTypeItem } from "../../models/salary/salaryRowTypeItem";
import { ICustomerItem } from "../../models/customer/customerItem";
import {
    WorkTimeReportGridRow,
    WorkTimeReportRowDto,
} from "../../models/reporting/workTimeReport";
import { getWorkTimeReportData } from "../../services/workHourSalaryService";
import { employeeGroupListItemDto } from "../../models/employeeGroup/employeeGroup";
import { showApiError } from "../../components/framework/formUtils";

export interface WorkTimeReportData {
    loading: boolean;
    vehicles: VehicleListItemDto[];
    employees: EmployeeListItemDto[];
    employeeGroups: employeeGroupListItemDto[];
    costCenters: ICostCenterItem[];
    customers: ICustomerItem[];
    workTimeTypes: WorkTimeType[];
    workTimeReportData: WorkTimeReportGridRow[];
    salaryRowTypes: SalaryRowTypeItem[];
}

export const useWorkTimeReportData = (): WorkTimeReportData => {
    const [workTimeReportDataLoading, setWorkTimeReportDataLoading] =
        useState(false);
    const [workTimeReportData, setWorkTimeReportData] =
        useState<WorkTimeReportRowDto[]>();

    const dispatch = useAppDispatch();

    const timeRange = useAppSelector(
        (state) => state.reportOptions.workTimeFilters.timeRange
    );
    const selectedEmployeeGroups = useAppSelector(
        (state) => state.reportOptions.workTimeFilters.selectedEmployeeGroups
    );

    const vehicles = useAppSelector((state) => state.reportOptions.vehicles);
    const employees = useAppSelector((state) => state.reportOptions.employees);
    const employeeGroups = useAppSelector(
        (state) => state.reportOptions.employeeGroups
    );
    const salaryRowTypes = useAppSelector(
        (state) => state.reportOptions.salaryRowTypes
    );
    const costCenters = useAppSelector(
        (state) => state.reportOptions.costCenters
    );
    const customers = useAppSelector((state) => state.reportOptions.customers);
    const workTimeTypes = useAppSelector(
        (state) => state.reportOptions.workTimeTypes
    );

    const selectedVehicles = useAppSelector(
        (state) => state.reportOptions.workTimeFilters.selectedVehicles
    );
    const selectedCostCenters = useAppSelector(
        (state) => state.reportOptions.workTimeFilters.selectedCostCenters
    );
    const selectedEmployees = useAppSelector(
        (state) => state.reportOptions.workTimeFilters.selectedEmployees
    );
    const selectedCustomers = useAppSelector(
        (state) => state.reportOptions.workTimeFilters.selectedCustomers
    );
    const selectedWorkTimeTypes = useAppSelector(
        (state) => state.reportOptions.workTimeFilters.selectedWorkTimeTypes
    );

    const gridRows = useMemo(
        () =>
            workTimeReportData?.map((d) => {
                const vehicle = vehicles?.find((e) => e.id === d.vehicleId);

                return {
                    ...d,
                    // When grouping by shift,
                    // use dateStr so different day rows without shift will not be
                    // grouped to together.
                    workShiftId: d.workShiftId ?? d.dateStr,
                    employeeName: employees?.find((e) => e.id === d.employeeId)
                        ?.fullName,
                    costCenterName: costCenters?.find(
                        (e) => e.id === d.costCenterId
                    )?.name,
                    customerName: customers?.find((e) => e.id === d.customerId)
                        ?.name,
                    vehicleName: !!vehicle
                        ? `${vehicle.registerNumber} - ${vehicle.brand}`
                        : null,
                    workTimeTypeName: workTimeTypes?.find(
                        (e) => e.workTimeTypeId === d.workTimeTypeId
                    )?.name,
                    salaryRows: d.salaryRows.map((sr) => ({
                        ...sr,
                        salaryRowTypeName: salaryRowTypes?.find(
                            (t) => t.id === sr.salaryRowTypeId
                        )?.name,
                    })),
                    totals: d.salaryRows.reduce(
                        (acc, curr) => {
                            if (!!curr.salaryRowTypeId) {
                                acc[curr.salaryRowTypeId] =
                                    (acc[curr.salaryRowTypeId] ?? 0) +
                                    curr.amount;
                            }
                            return acc;
                        },
                        {
                            [d.workTimeTypeId ?? "wtt_null"]: d.duration,
                            duration: d.duration,
                        }
                    ),
                } as WorkTimeReportGridRow;
            }),
        [
            workTimeReportData,
            salaryRowTypes,
            employees,
            vehicles,
            costCenters,
            customers,
            workTimeTypes,
        ]
    );

    const emptyWorkTimeType = {
        workTimeTypeId: null,
        name: "Ei työvaihetta",
        type: null,
    };

    const workTimeTypesWithEmptyType = useMemo(
        () => [...(workTimeTypes ?? []), emptyWorkTimeType],
        [workTimeTypes]
    );

    const loadWorkTimeReportData = useCallback(
        (signal?: AbortSignal) => {
            if (!timeRange[0] || !timeRange[1]) return;

            setWorkTimeReportDataLoading(true);

            getWorkTimeReportData(
                {
                    employeeGroupIds: selectedEmployeeGroups,
                    employeeIds: selectedEmployees,
                    startDate: timeRange[0],
                    endDate: timeRange[1],
                    vehicleIds: selectedVehicles,
                    customerIds: selectedCustomers,
                    costCenterIds: selectedCostCenters,
                    workTimeTypeIds: selectedWorkTimeTypes,
                },
                signal
            )
                .then((res) => setWorkTimeReportData(res?.data ?? []))
                .catch((err) => {
                    if (!signal?.aborted) {
                        handleApiError(err, dispatch);
                        console.log("error", err);
                    }
                })
                .finally(() => setWorkTimeReportDataLoading(false));
        },
        [
            selectedEmployeeGroups,
            selectedEmployees,
            timeRange[0],
            timeRange[1],
            selectedVehicles,
            selectedCustomers,
            selectedCostCenters,
            selectedWorkTimeTypes,
        ]
    );

    useEffect(() => {
        const abortController = new AbortController();

        void loadWorkTimeReportData(abortController.signal);

        return () => {
            abortController.abort();
        };
    }, [loadWorkTimeReportData]);

    useAsyncThunkAction(
        () => fetchReportEmployeeGroups(),
        { onError: showApiError },
        []
    );

    useAsyncThunkAction(
        () => fetchReportVehicles(),
        { onError: showApiError },
        []
    );

    useAsyncThunkAction(
        () => fetchReportEmployees(),
        { onError: showApiError },
        []
    );

    useAsyncThunkAction(
        () => fetchReportSalaryRowTypes(),
        { onError: showApiError },
        []
    );

    useAsyncThunkAction(
        () => fetchReportCostCenters(),
        { onError: showApiError },
        []
    );

    useAsyncThunkAction(
        () => fetchReportCustomers(),
        { onError: showApiError },
        []
    );

    useAsyncThunkAction(
        () => fetchReportWorkTimeTypes(),
        { onError: showApiError },
        []
    );

    useAsyncThunkAction(
        () => {
            if (!timeRange[0] || !timeRange[1]) {
                return;
            }
            return fetchCalendarDays({
                startDate: timeRange[0],
                endDate: timeRange[1],
            });
        },
        { onError: () => null },
        [timeRange[0], timeRange[1]]
    );

    return {
        loading: workTimeReportDataLoading,
        vehicles,
        employees,
        employeeGroups,
        costCenters,
        customers,
        workTimeTypes: workTimeTypesWithEmptyType,
        workTimeReportData: gridRows,
        salaryRowTypes,
    };
};
